import { Button, Card, Checkbox, Form, Modal, Popconfirm, Select, Input, Tooltip, message } from 'antd';
import { useEffect, useState } from 'react';

// Import styles
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";

import { programmes } from "../../utils/functions/apiConstants";
import { useSaveVideoGuide } from "../../hooks/userGuides";

import {
  ActivityIndicator,
  Notify,
  openNotification,
  util,
} from '@seims/shared';

const UserGuidesDetails = (props) => {

    // const [message, messageContextHolder] = reactMessage.useMessage();
    const {type: modalType, record: videoRecord, onShow} = props;
    const saveMutation = useSaveVideoGuide();

    const initWidth = 700;
    const { Option } = Select;

    const [disabled, setDisabled] = useState(true);
    const docTitle = `${modalType === "add" ? "Add" : modalType === "edit" ? "Edit" : "View"} Video Guide`;
    
    const [modalWidth, setModalWidth] = useState(initWidth);


    const [recordLoaded, setRecordLoaded] = useState(false);
    
    const [toolTipMessageSave, setToolTipMessageSave] = useState("Please complete the form");

    const [submitted, setSubmitted] = useState(false);
    const [progressMessage, setProgressMessage] = useState("");
    const [form] = Form.useForm();
    const formName = "VideoGuide";

    const [previewUrl, setPreviewUrl] = useState(null);
    const formValues = Form.useWatch([], form);
    const mediaIdValue = Form.useWatch('mediaId', form);
    const videoTitleValue = Form.useWatch('title', form);


    useEffect(() => {

        if (videoRecord && !recordLoaded) {
            onFormLoad();
            setRecordLoaded(true);
        };

        form.validateFields({validateOnly: true}).then(() => {
            setDisabled(false);
            setToolTipMessageSave("");
        }).catch(() => {
            setDisabled(true);
            setToolTipMessageSave("Please complete the form");
        })

        window.addEventListener('resize', () => {
            const screenWidth = window.innerWidth;
            if (screenWidth < initWidth) {
                setModalWidth(screenWidth * 0.8);
            }
        })
    }, [videoRecord, window, formValues, form, recordLoaded]);

    const confirmCancel = () => {
        setRecordLoaded(false);
        setSubmitted(false);
        form.resetFields();
        onShow(false);
        setDisabled(true);
        setPreviewUrl(null);
    };

    const handleCancel = () => {
        if (mediaIdValue || videoTitleValue) {
            setDisabled(false);
        } else {
            confirmCancel();
        }
    };

    const submitVideoGuide = async (values) => {

        const payload = {
            ...values,
            uuid: videoRecord?.uuid
        };

        setSubmitted(true);
        await saveMutation.mutateAsync(payload)
        .then(() => {
            onShow(false);
            form.resetFields();
            setDisabled(true);
            setToolTipMessageSave("Please complete the form")
        })
        .catch(() => {}) // Handled by mutation onError
        .finally(()=> {
            setSubmitted(false);
            setPreviewUrl(null);
        });
    };
    const onFormLoad = () => {
        const data = {...videoRecord};

        if (!util.isEmpty(data)) {
            const matchedProgramme = programmes.find(p => p.value === data.targetModule);
            form.setFieldsValue({
              uuid: data.uuid,
              targetModule: matchedProgramme?.key ?? data?.targetModule,
              mediaId: data?.mediaId,
              provider: data?.provider,
              title: data?.title,
              archived: data?.archived
            });
        }
    };

    const parseVideoId = (input) => {
        if (!input) return null;

        // 1. Check if it's already just an 11-char ID
        const idRegex = /^[a-zA-Z0-9_-]{11}$/;
        if (idRegex.test(input)) {
            return input;
        }

        // 2. If it's a URL, extract the ID
        const urlRegExp = /^.*(youtu.be\/|v\/|u\/|w\/|embed\/|watch\?v=|&v=|shorts\/)([^#&?]*).*/;
        const match = input.match(urlRegExp);

        if (match && match[2].length === 11) { // Note: Always confirm YT hasn't changed the ID format.
            return match[2];
        }

        return null;
    };

    const handlePreviewVideo = () => {
        const cleanId = parseVideoId(mediaIdValue);

        if (!cleanId) {
            openNotification(
                "Invalid Video Input", 
                "Please enter a valid 11-character YouTube Video ID or a full YouTube URL", 
                Notify.WARNING
            );
            setPreviewUrl(null);
            return;
        }
        form.setFieldsValue({ mediaId: cleanId });

        const embed = `https://www.youtube.com/embed/${cleanId}?rel=0&modestbranding=1`;
        setPreviewUrl(embed);
    };

    const onVideoIdChange = (e) =>  {
        const extracted = parseVideoId(e.target.value);
            if (extracted && extracted !== e.target.value) {
                form.setFieldsValue({ mediaId: extracted });
            }
    }

    const [isValidating, setIsValidating] = useState(false);

    const validateYouTubeId = async (videoId) => {
        try {            
            const response = await fetch(`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}`);
            
            const data = await response.json();
            
            // If the video is invalid/private/deleted, noembed returns an error
            if (data?.error) {
                return false;
            }
            return true;
        } catch (error) {
            console.error("Error validating video:", error);
            return false;
        }
    };


    const customMediaIdValidator = async (_, value) => {
        // 1. Exit early if it's empty (the 'required' rule handles this)
        // or if it's under 11 characters (the 'min' rule handles this).
        if (!value || value.length < 11) {
            return Promise.resolve();
        }

        // 2. Parse the ID just in case they pasted a full URL directly here
        const cleanId = parseVideoId(value);

        // If parsing fails or the extracted ID isn't exactly 11 chars
        if (!cleanId || cleanId.length !== 11) {
            const errMsg = "Invalid YouTube ID format";
            message.warning(errMsg);
            return Promise.reject(new Error(errMsg));
        }

        // 3. Make the API call
        const isRealVideo = await validateYouTubeId(cleanId);
        
        if (!isRealVideo) {
            const errMsg = "Video not found or is private";
            message.warning(errMsg);
            return Promise.reject(new Error(errMsg));
        }

        // 4. If all is good, resolve!
        return Promise.resolve();
    }


    return (
        <>
            <Modal
                className='doc-repo'
                key={"dd-main-modal"}
                title={docTitle}
                open={!!videoRecord}
                closable={false}
                keyboard={false}
                maskClosable={false}
                width={modalWidth}

                footer={[
                    <div className="modal-footer-buttons" key={"main-div-dd"}>
                        {modalType === "view" && (
                            <Button
                                onClick={() => confirmCancel()}
                                style={{ marginLeft: 10 }}
                            >
                                Okay
                            </Button>
                        )}
                        {(modalType === "add" || modalType === "edit") && (

                                <Tooltip title={toolTipMessageSave} key="2tooltip">
                                    <Button
                                        disabled={disabled}
                                        key="dd-btn-2"
                                        type="primary"
                                        htmlType="submit"
                                        form={formName}
                                        style={{ marginLeft: 10 }}
                                    >
                                        Save
                                    </Button>
                                </Tooltip>

                            )}

                        <Popconfirm
                            key="dd-pop-1"
                            title="Are you sure you want to cancel...you will lose all info entered?"
                            okText="Yes"
                            cancelText="No"
                            onConfirm={() => confirmCancel()}
                            onCancel={() => handleCancel()}
                            disabled={disabled}
                        >
                            {modalType !== "view" && (
                                <Button
                                    onMouseUp={() => handleCancel()}
                                    style={{ marginLeft: 10 }}
                                >
                                    Cancel
                                </Button>
                            )}

                        </Popconfirm>
                    </div >,
                ]}
            >
                <Form
                    key={"video-guide-main-form"}
                    disabled={modalType === "view"}
                    labelCol={{
                        span: 6,
                    }}
                    wrapperCol={{
                        span: 18,
                    }}
                    form={form}
                    name={formName}
                    onFinish={submitVideoGuide}
                >
                    <Card>
                        <Form.Item
                                hidden={true}
                                name="uuid"
                                rules={[{ required: false}]}
                                >
                                <Input />
                            </Form.Item>
                        <Form.Item
                            name="targetModule"
                            label="Target Module"
                            rules={[
                                {
                                    required: true,
                                    message: "Please select target module",
                                },
                            ]}
                        >
                            <Select
                                placeholder="Select a option and change input text above"
                                allowClear
                            >
                                {programmes.map((module) => {
                                    return <Option key={module.key} value={module.key}>{module.value}</Option>;
                                })}
                            </Select>
                        </Form.Item>
                        <Form.Item
                            name="title"
                            label="Media Title"
                            rules={[{ required: true, message: "Please enter video title" }]}
                            >
                            <Input name="videoTitle" />
                        </Form.Item>
                        <Form.Item
                            name="url"
                            label="Media URL"
                            rules={[{ required: false}]}
                            >
                            <Input
                                    placeholder="Paste YouTube URL to extract Video ID"
                                    onChange={onVideoIdChange}
                                />
                        </Form.Item>
                        <Form.Item
                            name="mediaId"
                            label="Media ID"
                            rules={[
                                { required: true, message: "Please enter a valid video ID" },
                                { min: 11,  message: "The ID must be at least 11 characters"},
                                { validator: customMediaIdValidator }
                            ]}
                            >
                            <Input.Search
                                    placeholder="https://www.youtube.com/watch?v=<VIDEO ID>"
                                    enterButton={
                                        <Button 
                                            type="primary" 
                                            // Disables the button if the string is empty or just whitespace
                                            disabled={!mediaIdValue}
                                            loading={isValidating}
                                        >
                                            Preview Video
                                        </Button>
                                    } onSearch={handlePreviewVideo}
                                />
                        </Form.Item>

                        <Form.Item
                            name="archived"
                            label="Archived"
                            valuePropName="checked"
    
                        >
                            <Checkbox />
    
                        </Form.Item>

                        { previewUrl && (
                            <Card>
                                <div>
                                    <div
                                    style={{ marginBottom: "20px", textAlign: "center"}}
                                    >
                                        <h3>Video Preview: {videoTitleValue ?? "Provide Video Title"}</h3>
                                    </div>
                                    <hr style={{width: '65%'}}/>
                                    <div
                                    style={{
                                        position: "relative",
                                        marginTop: "20px",
                                        paddingBottom: "56.25%",
                                        height: 0,
                                    }}
                                    >
                                    <iframe
                                        title={videoTitleValue ?? "Video Preview"}
                                        src={previewUrl}
                                        style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: "100%",
                                        height: "100%",
                                        }}
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    />
                                    </div>
                                </div>
                            </Card>
                        )}
                        {(submitted === true) && (
                            <>
                                <hr />
                                <ActivityIndicator visible={true} message={progressMessage} />
                            </>
                        )}
                    </Card>
                </Form>
            </Modal >
        </>
    )
}

export default UserGuidesDetails;
